import { apiClient } from "api/trpc/trpc-client"
import Loader from "reusable/mobx-helpers/Loader"
import { inferProcedureOutput } from "@trpc/server"
import { AppRouter } from "@trpc-endpoints/trpc/router"
import { makeAutoObservable } from "mobx"
import ModalState from "reusable/mobx-helpers/ModalState"
import { GridColDef, GridEventListener } from "@mui/x-data-grid"
import { CopyButton } from "./DuplicateUserSearch"

export type DuplicateUser = inferProcedureOutput<AppRouter['user']['findDuplicateUsers']>[number]
export type DuplicateUserRow = DuplicateUser['users'][number]

export default class DuplicateUserSearchStore {
   userLoader = new Loader()
   duplicateUserLoader = new Loader()
   duplicateUsers: DuplicateUser[] = []
   selectedEmail: DuplicateUser | null = null
   detailsModal = new ModalState()

   constructor() {
      makeAutoObservable(this)
   }

   get detailsRows(): DuplicateUserRow[] {
      return (this.selectedEmail?.users || []).map(u => ({ ...u, id: u.userId }))
   }

   get mainColumns(): GridColDef<DuplicateUser>[] {
      return [
         {
            field: 'email',
            headerName: 'Email',
            flex: 1,
            minWidth: 300,
         },
         {
            field: 'count',
            headerName: 'Duplicate Count',
            flex: 0,
            minWidth: 150,
         }
      ]
   }

   get detailsColumn(): GridColDef<DuplicateUserRow>[] {
      return [{
         field: 'userId',
         headerName: 'User ID',
         flex: 1,
         minWidth: 200,
      },
      {
         field: 'email',
         headerName: 'Email',
         flex: 1,
         minWidth: 200,
      },
      {
         field: 'lastLogin',
         headerName: 'Last Login',
         flex: 1,
         minWidth: 150,
         valueGetter: (_value, row) => row.lastLogin ? new Date(row.lastLogin).getTime() : 0,
         renderCell: ({ row }) => row.lastLogin ? new Date(row.lastLogin).toLocaleString() : 'Unknown',
      },
      {
         field: 'actions',
         headerName: 'Actions',
         sortable: false,
         align: 'center',
         maxWidth: 100,
         renderCell: ({ row }) => CopyButton(row.email)
      }]
   }

   searchDuplicateUsers = async () => {
      await this.duplicateUserLoader.executeLoading(async () => {
         const duplicateUsers = await apiClient.user.findDuplicateUsers.query()
         this.duplicateUsers = duplicateUsers.map(u => ({ ...u, id: u.email }))
      })
   }

   handleMainRowClick: GridEventListener<"rowClick"> = (params) => {
      const duplicate = this.duplicateUsers.find(d => d.email === params.row.email)
      if (duplicate) {
         this.selectedEmail = duplicate
         this.detailsModal.open()
      }
   }
}

